/**
 * OverviewPage - Full-featured overview with modals
 * Features: Event info card, RSVP stats, Activity log, Schedule CRUD, Notes, Invite collaborator
 * BUILD: 2026-05-09-v1
 */
import React, { useState } from 'react';
import { eventService } from '../../services/api';
import toast from 'react-hot-toast';

// Debug log to verify deployment
console.log('🔧 Dashboard/OverviewPage BUILD 2026-05-10-v1');


// Note category colors
const noteCategoryColors = [
  '#1e514b', // teal
  '#b8860b', // gold
  '#9d174d', // pink
  '#3730a3', // indigo
  '#166534', // green
  '#9a3412', // orange
];

// Helpers
const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '';
const formatDateInput = (d) => {
  if (!d) return '';
  const date = new Date(d);
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
};
const timeAgo = (d) => {
  if (!d) return '';
  const mins = Math.floor((Date.now() - new Date(d)) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  if (mins < 2880) return 'yesterday';
  return formatDate(d);
};

// Format activity - uses backend's description field directly
const formatActivity = (activity) => {
  const { action, resourceType, description, changes, userId } = activity;
  const userName = userId?.name || userId?.email?.split('@')[0] || '';

  // The backend already provides a good description field
  // e.g., 'Added schedule event "sangeet"', 'Updated guest: John'
  let displayText = description || '';

  // Add time details from changes if available (for schedule events)
  if (resourceType === 'schedule_event' && changes) {
    const time = changes.time || changes.new?.time;
    const venue = changes.venue || changes.new?.venue;
    if (time) {
      displayText += ` at ${time}`;
    }
    if (venue) {
      displayText += ` • ${venue}`;
    }
  }

  // Determine icon type based on resourceType
  const iconType =
    resourceType === 'guest' ? 'check' :
    resourceType === 'schedule_event' ? 'calendar' :
    resourceType === 'task' ? 'check' :
    resourceType === 'vendor' ? 'message' :
    resourceType === 'ai_response' ? 'sparkles' :
    'message';

  // Determine dot color
  const dotClass =
    resourceType === 'guest' || action === 'confirmed' ? 'green' :
    resourceType === 'ai_response' ? 'gold' :
    '';

  return { text: displayText, userName, iconType, dotClass };
};

// Icon component
const Icon = ({ name, size = 16 }) => {
  const icons = {
    check: <><polyline points="20 6 9 17 4 12"/></>,
    sparkles: <><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></>,
    message: <><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></>,
    plus: <><path d="M5 12h14M12 5v14"/></>,
    calendar: <><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
    edit: <><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></>,
    trash: <><path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></>,
    x: <><path d="M18 6 6 18M6 6l12 12"/></>,
    users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></>,
    mail: <><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></>,
    fileText: <><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" x2="8" y1="13" y2="13"/><line x1="16" x2="8" y1="17" y2="17"/></>,
    mapPin: <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></>,
    phone: <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {icons[name] || icons.message}
    </svg>
  );
};

// Modal wrapper
const Modal = ({ isOpen, onClose, title, children, width = 480 }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 10000
    }}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 16, width, maxWidth: '90vw', maxHeight: '90vh',
        overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
      }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--c-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <Icon name="x" size={20} />
          </button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
};

// Form field component
const Field = ({ label, children, required }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--c-fg-muted)' }}>
      {label} {required && <span style={{ color: 'var(--c-destructive)' }}>*</span>}
    </label>
    {children}
  </div>
);

const inputStyle = {
  width: '100%', padding: '10px 12px', border: '1px solid var(--c-border)',
  borderRadius: 8, fontSize: 14, outline: 'none', fontFamily: 'inherit'
};

// ============ OVERVIEW PAGE ============
function OverviewPage({ event, onRefresh, eventId }) {
  const [editEventModal, setEditEventModal] = useState(false);
  const [scheduleModal, setScheduleModal] = useState({ open: false, event: null, index: null });
  const [noteModal, setNoteModal] = useState({ open: false, note: null, index: null });
  const [inviteModal, setInviteModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Event form state
  const [eventForm, setEventForm] = useState({});

  // Schedule form state
  const [scheduleForm, setScheduleForm] = useState({ name: '', date: '', time: '', venue: '' });

  // Note form state
  const [noteForm, setNoteForm] = useState({ title: '', content: '' });

  // Invite form state
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'viewer' });

  const guests = event?.guests || [];
  const scheduleEvents = event?.scheduleEvents || [];
  const activityLogs = event?.activityLogs || [];
  const notes = event?.notes || [];

  const plusOnesFor = (filter) => guests.filter(filter).reduce((sum, g) => sum + (g.plusOnes || 0), 0);
  const confirmed = guests.filter(g => g.rsvp === 'yes').length + plusOnesFor(g => g.rsvp === 'yes');
  const pending = guests.filter(g => g.rsvp === 'pending').length + plusOnesFor(g => g.rsvp === 'pending');
  const notinvited = guests.filter(g => g.rsvp === 'unassigned').length + plusOnesFor(g => g.rsvp === 'unassigned');
  const declined = guests.filter(g => g.rsvp === 'no').length + plusOnesFor(g => g.rsvp === 'no');
  const total = guests.length + guests.reduce((sum, g) => sum + (g.plusOnes || 0), 0);
  const invited = confirmed + pending + declined;
  const confirmRate = invited > 0 ? Math.round((confirmed / invited) * 100) : 0;

  // Open edit event modal
  const openEditEvent = () => {
    setEventForm({
      name: event.name || '',
      brideName: event.brideName || '',
      groomName: event.groomName || '',
      startDate: formatDateInput(event.startDate),
      endDate: formatDateInput(event.endDate),
      venue: event.venue || '',
      budget: event.budget || '',
      pointOfContactName: event.pointOfContactName || '',
      pointOfContactNumber: event.pointOfContactNumber || '',
    });
    setEditEventModal(true);
  };

  // Save event
  const saveEvent = async () => {
    setSaving(true);
    try {
      await eventService.updateEvent(eventId, eventForm);
      toast.success('Event updated');
      setEditEventModal(false);
      onRefresh();
    } catch (err) {
      toast.error('Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  // Open schedule modal
  const openScheduleModal = (schedEvent = null, index = null) => {
    if (schedEvent) {
      setScheduleForm({
        name: schedEvent.name || '',
        date: formatDateInput(schedEvent.date),
        time: schedEvent.time || '',
        venue: schedEvent.venue || '',
      });
    } else {
      setScheduleForm({ name: '', date: formatDateInput(event.startDate), time: '', venue: event.venue || '' });
    }
    setScheduleModal({ open: true, event: schedEvent, index });
  };

  // Save schedule event
  const saveScheduleEvent = async () => {
    if (!scheduleForm.name || !scheduleForm.date) {
      toast.error('Name and date are required');
      return;
    }
    setSaving(true);
    try {
      if (scheduleModal.index !== null) {
        await eventService.updateScheduleEvent(eventId, scheduleModal.index, scheduleForm);
        toast.success('Schedule event updated');
      } else {
        await eventService.addScheduleEvent(eventId, scheduleForm);
        toast.success('Schedule event added');
      }
      setScheduleModal({ open: false, event: null, index: null });
      onRefresh();
    } catch (err) {
      toast.error('Failed to save schedule event');
    } finally {
      setSaving(false);
    }
  };

  // Delete schedule event
  const deleteScheduleEvent = async () => {
    if (scheduleModal.index === null) return;
    setSaving(true);
    try {
      await eventService.deleteScheduleEvent(eventId, scheduleModal.index);
      toast.success('Schedule event deleted');
      setScheduleModal({ open: false, event: null, index: null });
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete schedule event');
    } finally {
      setSaving(false);
    }
  };

  // Open note modal
  const openNoteModal = (note = null, index = null) => {
    if (note) {
      setNoteForm({ title: note.title || '', content: note.content || '' });
    } else {
      setNoteForm({ title: '', content: '' });
    }
    setNoteModal({ open: true, note, index });
  };

  // Save note
  const saveNote = async () => {
    if (!noteForm.title) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      if (noteModal.note?._id) {
        await eventService.updateNote(eventId, noteModal.note._id, noteForm);
        toast.success('Note updated');
      } else {
        await eventService.addNote(eventId, noteForm);
        toast.success('Note added');
      }
      setNoteModal({ open: false, note: null, index: null });
      onRefresh();
    } catch (err) {
      toast.error('Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  // Delete note
  const deleteNote = async () => {
    if (!noteModal.note?._id) return;
    setSaving(true);
    try {
      await eventService.deleteNote(eventId, noteModal.note._id);
      toast.success('Note deleted');
      setNoteModal({ open: false, note: null, index: null });
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete note');
    } finally {
      setSaving(false);
    }
  };

  // Send invite
  const sendInvite = async () => {
    if (!inviteForm.email) {
      toast.error('Email is required');
      return;
    }
    setSaving(true);
    try {
      await eventService.inviteUser(eventId, inviteForm);
      toast.success('Invitation sent');
      setInviteModal(false);
      setInviteForm({ email: '', role: 'viewer' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send invitation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Event Info Card */}
      <div className="panel" style={{ marginBottom: 24 }}>
        <div className="panel-body" style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 24 }}>{event.name}</h2>
              <span style={{ background: 'var(--c-success)', color: '#fff', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>Active</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 32px', fontSize: 14, color: 'var(--c-fg-muted)' }}>
              {event.brideName && event.groomName && (
                <div><Icon name="users" size={14} /> {event.brideName} & {event.groomName}</div>
              )}
              <div><Icon name="calendar" size={14} /> {formatDate(event.startDate)}{event.endDate && event.endDate !== event.startDate ? ` - ${formatDate(event.endDate)}` : ''}</div>
              {event.venue && <div><Icon name="mapPin" size={14} /> {event.venue}</div>}
              {event.pointOfContactName && <div><Icon name="phone" size={14} /> {event.pointOfContactName} {event.pointOfContactNumber && `(${event.pointOfContactNumber})`}</div>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-secondary" onClick={openEditEvent}><Icon name="edit" size={14} /> Edit</button>
            <button className="btn-secondary" onClick={() => setInviteModal(true)}><Icon name="users" size={14} /> Invite</button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-l">Confirmed</div>
          <div className="stat-n teal">{confirmed}</div>
          <div className="stat-foot">{confirmRate}% of invited</div>
        </div>
        <div className="stat-card">
          <div className="stat-l">Pending</div>
          <div className="stat-n gold">{pending}</div>
          <div className="stat-foot">Awaiting response</div>
        </div>
        <div className="stat-card">
          <div className="stat-l">Declined</div>
          <div className="stat-n">{declined}</div>
          <div className="stat-foot">Not attending</div>
        </div>
        <div className="stat-card">
          <div className="stat-l">Not Invited</div>
          <div className="stat-n">{notinvited}</div>
          <div className="stat-foot">RSVP not sent</div>
        </div>
        <div className="stat-card">
          <div className="stat-l">Total</div>
          <div className="stat-n">{total}</div>
          <div className="stat-foot">{invited} invited</div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="row-2">
        {/* Schedule - Redesigned */}
        <div className="panel" style={{ display: 'flex', flexDirection: 'column', maxHeight: 420 }}>
          <div className="panel-head">
            <div className="panel-title">Schedule</div>
            <button className="btn-primary" onClick={() => openScheduleModal()}>
              <Icon name="plus" size={14} /> Add
            </button>
          </div>
          <div className="panel-body" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: scheduleEvents.length > 0 ? '8px 16px 16px' : '16px' }}>
            {scheduleEvents.length === 0 ? (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                padding: '40px 20px', color: 'var(--c-fg-muted)', textAlign: 'center'
              }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 16, background: 'var(--c-bg-subtle)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16
                }}>
                  <Icon name="calendar" size={24} />
                </div>
                <div style={{ fontWeight: 500, marginBottom: 4 }}>No events scheduled</div>
                <div style={{ fontSize: 13 }}>Add your wedding events to the schedule</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {scheduleEvents.map((e, i) => {
                  const date = new Date(e.date);
                  return (
                    <div
                      key={i}
                      onClick={() => openScheduleModal(e, i)}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 14, padding: '12px 14px',
                        background: '#fff', borderRadius: 12, cursor: 'pointer',
                        border: '1px solid var(--c-border)',
                        transition: 'all 0.15s ease',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                      }}
                      onMouseEnter={ev => { ev.currentTarget.style.borderColor = 'var(--c-primary)'; ev.currentTarget.style.boxShadow = '0 2px 8px rgba(30,81,75,0.08)'; }}
                      onMouseLeave={ev => { ev.currentTarget.style.borderColor = 'var(--c-border)'; ev.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
                    >
                      {/* Date Badge */}
                      <div style={{
                        minWidth: 48, height: 52, borderRadius: 10,
                        background: 'linear-gradient(135deg, var(--c-primary) 0%, #2a6b63 100%)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', flexShrink: 0
                      }}>
                        <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1 }}>{date.getUTCDate()}</div>
                        <div style={{ fontSize: 10, letterSpacing: '.05em', opacity: 0.9, marginTop: 2 }}>
                          {date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }).toUpperCase()}
                        </div>
                      </div>
                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ marginBottom: 4 }}>
                          <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--c-fg)' }}>{e.name}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: 'var(--c-fg-muted)' }}>
                          {(e.venue || event?.venue) && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Icon name="mapPin" size={12} />
                              {e.venue || event?.venue}
                            </span>
                          )}
                          {e.time && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                              </svg>
                              {e.time}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Arrow */}
                      <div style={{ color: 'var(--c-fg-muted)', opacity: 0.5 }}>
                        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="m9 18 6-6-6-6"/>
                        </svg>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Activity Log */}
        <div className="panel" style={{ display: 'flex', flexDirection: 'column', maxHeight: 400 }}>
          <div className="panel-head">
            <div className="panel-title">Recent activity</div>
            <button className="btn-secondary" onClick={onRefresh}>Refresh</button>
          </div>
          <div className="panel-body" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <div className="timeline">
              {activityLogs.length === 0 ? (
                <div style={{ color: 'var(--c-fg-muted)', padding: '20px 0', textAlign: 'center' }}>No recent activity</div>
              ) : (
                activityLogs.slice(0, 10).map((a, i) => {
                  const formatted = formatActivity(a);
                  return (
                    <div className="tl-item" key={a._id || i}>
                      <div className={`tl-dot ${formatted.dotClass}`}>
                        <Icon name={formatted.iconType} size={14} />
                      </div>
                      <div className="tl-body">
                        <span>{formatted.text}</span>
                        <div className="tl-time">
                          {timeAgo(a.timestamp)}
                          {formatted.userName && ` • ${formatted.userName}`}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Notes Section - Redesigned */}
      <div className="panel" style={{ marginTop: 24 }}>
        <div className="panel-head">
          <div className="panel-title">Notes</div>
          <button className="btn-primary" onClick={() => openNoteModal()}>
            <Icon name="plus" size={14} /> Add note
          </button>
        </div>
        <div className="panel-body">
          {notes.length === 0 ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              padding: '48px 20px', color: 'var(--c-fg-muted)', textAlign: 'center'
            }}>
              <div style={{
                width: 56, height: 56, borderRadius: 16, background: 'var(--c-bg-subtle)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16
              }}>
                <Icon name="fileText" size={24} />
              </div>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>No notes yet</div>
              <div style={{ fontSize: 13 }}>Add notes to keep track of important details</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {notes.map((note, i) => {
                const borderColor = noteCategoryColors[i % noteCategoryColors.length];
                return (
                  <div
                    key={note._id || i}
                    onClick={() => openNoteModal(note, i)}
                    style={{
                      background: '#fff', borderRadius: 12, cursor: 'pointer',
                      border: '1px solid var(--c-border)', overflow: 'hidden',
                      transition: 'all 0.15s ease',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                      display: 'flex'
                    }}
                    onMouseEnter={ev => { ev.currentTarget.style.borderColor = borderColor; ev.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; ev.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={ev => { ev.currentTarget.style.borderColor = 'var(--c-border)'; ev.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; ev.currentTarget.style.transform = 'translateY(0)'; }}
                  >
                    {/* Colored left border */}
                    <div style={{ width: 4, background: borderColor, flexShrink: 0 }} />
                    {/* Content */}
                    <div style={{ padding: '16px 16px 14px', flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}>{note.title}</div>
                        <div style={{ color: 'var(--c-fg-muted)', opacity: 0.5, flexShrink: 0 }}>
                          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="m9 18 6-6-6-6"/>
                          </svg>
                        </div>
                      </div>
                      <div style={{
                        fontSize: 13, color: 'var(--c-fg-muted)', lineHeight: 1.5,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                      }}>
                        {note.content || 'No content'}
                      </div>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8, marginTop: 12,
                        fontSize: 11, color: 'var(--c-fg-muted)'
                      }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          background: 'var(--c-bg-subtle)', padding: '2px 8px', borderRadius: 4
                        }}>
                          <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                          </svg>
                          {timeAgo(note.createdAt)}
                        </span>
                        {note.category && (
                          <span style={{ background: 'var(--c-bg-subtle)', padding: '2px 8px', borderRadius: 4 }}>
                            {note.category}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Edit Event Modal */}
      <Modal isOpen={editEventModal} onClose={() => setEditEventModal(false)} title="Edit Event" width={560}>
        <Field label="Event Name" required>
          <input style={inputStyle} value={eventForm.name || ''} onChange={e => setEventForm({...eventForm, name: e.target.value})} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Bride's Name">
            <input style={inputStyle} value={eventForm.brideName || ''} onChange={e => setEventForm({...eventForm, brideName: e.target.value})} />
          </Field>
          <Field label="Groom's Name">
            <input style={inputStyle} value={eventForm.groomName || ''} onChange={e => setEventForm({...eventForm, groomName: e.target.value})} />
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Start Date" required>
            <input type="date" style={inputStyle} value={eventForm.startDate || ''} onChange={e => setEventForm({...eventForm, startDate: e.target.value})} />
          </Field>
          <Field label="End Date">
            <input type="date" style={inputStyle} value={eventForm.endDate || ''} onChange={e => setEventForm({...eventForm, endDate: e.target.value})} />
          </Field>
        </div>
        <Field label="Venue">
          <input style={inputStyle} value={eventForm.venue || ''} onChange={e => setEventForm({...eventForm, venue: e.target.value})} />
        </Field>
        <Field label="Budget">
          <input type="number" style={inputStyle} value={eventForm.budget || ''} onChange={e => setEventForm({...eventForm, budget: e.target.value})} placeholder="e.g., 500000" />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Contact Name">
            <input style={inputStyle} value={eventForm.pointOfContactName || ''} onChange={e => setEventForm({...eventForm, pointOfContactName: e.target.value})} />
          </Field>
          <Field label="Contact Number">
            <input style={inputStyle} value={eventForm.pointOfContactNumber || ''} onChange={e => setEventForm({...eventForm, pointOfContactNumber: e.target.value})} placeholder="+91..." />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={() => setEditEventModal(false)}>Cancel</button>
          <button className="btn-primary" onClick={saveEvent} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </Modal>

      {/* Schedule Event Modal */}
      <Modal isOpen={scheduleModal.open} onClose={() => setScheduleModal({ open: false, event: null, index: null })} title={scheduleModal.event ? 'Edit Schedule Event' : 'Add Schedule Event'}>
        <Field label="Event Name" required>
          <input style={inputStyle} value={scheduleForm.name} onChange={e => setScheduleForm({...scheduleForm, name: e.target.value})} placeholder="e.g., Mehndi Ceremony" />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Date" required>
            <input type="date" style={inputStyle} value={scheduleForm.date} onChange={e => setScheduleForm({...scheduleForm, date: e.target.value})} />
          </Field>
          <Field label="Time">
            <input type="time" style={inputStyle} value={scheduleForm.time} onChange={e => setScheduleForm({...scheduleForm, time: e.target.value})} />
          </Field>
        </div>
        <Field label="Venue">
          <input style={inputStyle} value={scheduleForm.venue} onChange={e => setScheduleForm({...scheduleForm, venue: e.target.value})} placeholder="e.g., Grand Ballroom" />
        </Field>
        <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'space-between' }}>
          <div>
            {scheduleModal.event && (
              <button className="btn-secondary" style={{ color: 'var(--c-destructive)' }} onClick={deleteScheduleEvent} disabled={saving}>
                <Icon name="trash" size={14} /> Delete
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button className="btn-secondary" onClick={() => setScheduleModal({ open: false, event: null, index: null })}>Cancel</button>
            <button className="btn-primary" onClick={saveScheduleEvent} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      </Modal>

      {/* Note Modal */}
      <Modal isOpen={noteModal.open} onClose={() => setNoteModal({ open: false, note: null, index: null })} title={noteModal.note ? 'Edit Note' : 'Add Note'}>
        <Field label="Title" required>
          <input style={inputStyle} value={noteForm.title} onChange={e => setNoteForm({...noteForm, title: e.target.value})} placeholder="Note title" />
        </Field>
        <Field label="Content">
          <textarea style={{...inputStyle, minHeight: 150, resize: 'vertical'}} value={noteForm.content} onChange={e => setNoteForm({...noteForm, content: e.target.value})} placeholder="Write your note here..." />
        </Field>
        <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'space-between' }}>
          <div>
            {noteModal.note?._id && (
              <button className="btn-secondary" style={{ color: 'var(--c-destructive)' }} onClick={deleteNote} disabled={saving}>
                <Icon name="trash" size={14} /> Delete
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button className="btn-secondary" onClick={() => setNoteModal({ open: false, note: null, index: null })}>Cancel</button>
            <button className="btn-primary" onClick={saveNote} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      </Modal>

      {/* Invite Modal */}
      <Modal isOpen={inviteModal} onClose={() => setInviteModal(false)} title="Invite Collaborator">
        <p style={{ color: 'var(--c-fg-muted)', marginTop: 0, marginBottom: 20, fontSize: 14 }}>
          Invite team members to collaborate on this event. They'll receive an email with access instructions.
        </p>
        <Field label="Email Address" required>
          <input type="email" style={inputStyle} value={inviteForm.email} onChange={e => setInviteForm({...inviteForm, email: e.target.value})} placeholder="colleague@example.com" />
        </Field>
        <Field label="Role">
          <select style={inputStyle} value={inviteForm.role} onChange={e => setInviteForm({...inviteForm, role: e.target.value})}>
            <option value="viewer">Viewer - Can view but not edit</option>
            <option value="editor">Editor - Can view and edit</option>
          </select>
        </Field>
        <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={() => setInviteModal(false)}>Cancel</button>
          <button className="btn-primary" onClick={sendInvite} disabled={saving}>
            <Icon name="mail" size={14} /> {saving ? 'Sending...' : 'Send Invite'}
          </button>
        </div>
      </Modal>
    </>
  );
}

export default OverviewPage;
